import React from 'react'
import { Row, Column, Typography, H2, FlexBox } from '@vp/swan'
import { BFFPageData } from '../services/serviceClients/BffClient'
import ProductTileCollectionUtil, { CalloutDataType } from '../utils/productTileCollection/productTileCollection'
import { ProductGridType } from '../types/TemplateConfiguration'
import { useAppContext } from '../state/AppContext'
import { isProductTileAvailable, isProductTileInStock } from '../utils/ProductTile/productTile'
import { ProductGridLaneError, renderProductDebugError } from './errorComponents/ProductGridLaneError'
import { DebugPageSections } from './debugComponents/DebugPageSections'
import { ProductTileV2 } from './ProductTileV2'
import { LinkWrapper } from './LinkWrapper'
import { buildProductCallout } from '../utils/callouts/productCalloutBuilder'

interface ProductGridProps {
  section: ProductGridType
  pageDataFromBFF: BFFPageData
  sectionNumber: number
}

export const ProductGrid = ({ section, pageDataFromBFF, sectionNumber }: ProductGridProps) => {
  const { enableCampaignCallouts, sidebarEnabled, reviewsData, dictionaryEntries, debugMode, logger } = useAppContext()
  const layout = section.type === 'product grid' ? 'vertical' : 'horizontal'
  const spanXs = section.type === 'product grid' ? 6 : 12
  const pageZone =
    section.type === 'product grid'
      ? `Product Grid ${sectionNumber}`
      : section.type === 'product grid horizontal'
        ? 'Product Grid Horizontal'
        : 'Product Grid Horizontal XL'

  const model = ProductTileCollectionUtil.getModel(section, pageDataFromBFF)

  let tilesCount = model.tiles.length
  model.tiles.forEach((tile) => {
    if (!debugMode && !isProductTileAvailable(tile)) {
      tilesCount--
    }
  })

  const desktopTilePerRow = ProductTileCollectionUtil.getNumberOfProductTilesPerRow(sidebarEnabled, section.type, tilesCount)
  const colorSwatchCount = ProductTileCollectionUtil.getMaxColorSwatchCountDesktop(desktopTilePerRow, sidebarEnabled)
  const desktopColSpan = (12 / desktopTilePerRow) as 6 | 4 | 3 | 2
  const hideProductDescription = section.hideProductDescription

  if (!model.tiles.length || model.tiles.length === 0) {
    return <></>
  }

  return (
    <>
      <Row mb={4}>
        <Column span={sidebarEnabled ? 7 : 5}>{model.title && <H2 fontSize="x2large">{model.title.value}</H2>}</Column>
      </Row>
      <Row mb={7}>
        <Column span={sidebarEnabled ? 7 : 5}>{model.subtitle && <Typography fontSize="standard">{model.subtitle.value}</Typography>}</Column>
        <Column span={sidebarEnabled ? 5 : 7}>
          <FlexBox alignItems="flex-end" justifyContent="flex-end">
            {section.enableSeeAllCTA && section.seeAllCta?.targetUrl && dictionaryEntries.seeAllCtaText && (
              <LinkWrapper id="see-all-cta" skin="cta" href={section.seeAllCta?.targetUrl}>
                {dictionaryEntries.seeAllCtaText}
              </LinkWrapper>
            )}
          </FlexBox>
        </Column>
      </Row>
      <Row>
        {model.tiles.map((tile, index) => {
          if (isProductTileAvailable(tile)) {
            const callout = buildProductCallout({
              isOutOfStock: tile?.isOutOfStock ?? false,
              outOfStockText: dictionaryEntries?.['outOfStock'] || '',
              logger,
              callout: tile.product?.template?.calloutDetails as CalloutDataType,
              allAvailableTags: tile.product?.template?.allAvailableTags,
              tileId: tile.product?.contentful_id,
            })
            const tileProps = tile.product?.template
            const newTileMpvId = tileProps?.productsList?.selectedProduct?.mpvId
            return (
              <Column span={desktopColSpan} spanXs={spanXs} key={index}>
                <ProductTileV2
                  name={tileProps?.tileDetails?.header?.value ?? ''}
                  description={!hideProductDescription ? (tileProps?.tileDetails?.description?.value ?? '') : ''}
                  formattedDescription={ProductTileCollectionUtil.getFormattedDescription(
                    hideProductDescription,
                    tileProps?.tileDetails?.formattedDescription?.value,
                  )}
                  image={tileProps?.tileDetails?.image?.cloudinaryImage?.[0]}
                  linkUrl={tileProps?.tileDetails?.link?.href}
                  callout={callout}
                  layout={layout}
                  disabled={!isProductTileInStock(tile)}
                  index={index + 1}
                  pageZone={pageZone}
                  mpvId={newTileMpvId}
                  review={reviewsData?.[newTileMpvId as string]}
                  productId={tileProps?.productKey}
                  showPrice={!!tile.showPrice}
                  enableCampaignCallouts={enableCampaignCallouts}
                  colorInformation={tile.colorInformation}
                  colorSwatchCount={colorSwatchCount}
                  key={index}
                  version={tile.version}
                  selectedProductVariants={tileProps?.productsList?.selectedProductVariants}
                />
              </Column>
            )
          } else if (renderProductDebugError(tile)) {
            return (
              <Column span={desktopColSpan} spanXs={spanXs} key={index}>
                <ProductGridLaneError
                  tile={tile}
                  index={index}
                  productGridLayout={layout}
                  hideProductDescription={hideProductDescription}
                  colorSwatchCount={colorSwatchCount}
                  key={index}
                />
              </Column>
            )
          }
          return <React.Fragment key={index} />
        })}
      </Row>
      <DebugPageSections rawSectionData={section} resolvedSectionData={model} />
    </>
  )
}
